import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Linear/Vercel-style top progress bar.
 * Always mounted (lives outside Suspense), shows on route changes,
 * non-linear progression (fast at start, decays toward 90%, jumps to 100% on completion).
 */
export const TopProgressBar = () => {
  const location = useLocation();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const timersRef = useRef<number[]>([]);
  const firstRenderRef = useRef(true);

  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }

    // Reset
    timersRef.current.forEach(window.clearTimeout);
    timersRef.current = [];

    setVisible(true);
    setProgress(8);

    // Logarithmic-ish progression: fast start, slowing toward 90%
    timersRef.current.push(
      window.setTimeout(() => setProgress(35), 60),
      window.setTimeout(() => setProgress(60), 180),
      window.setTimeout(() => setProgress(78), 360),
      window.setTimeout(() => setProgress(88), 600),
      window.setTimeout(() => {
        setProgress(100);
        timersRef.current.push(
          window.setTimeout(() => setVisible(false), 180),
          window.setTimeout(() => setProgress(0), 400),
        );
      }, 850),
    );

    return () => {
      timersRef.current.forEach(window.clearTimeout);
    };
  }, [location.pathname]);

  return (
    <div
      aria-hidden
      className="fixed top-0 left-0 right-0 z-[200] h-[2px] pointer-events-none"
    >
      <div
        className="h-full bg-primary will-change-[width,opacity]"
        style={{
          width: `${progress}%`,
          opacity: visible ? 1 : 0,
          transition: "width 320ms cubic-bezier(0.16, 1, 0.3, 1), opacity 200ms ease-out",
          boxShadow:
            "0 0 8px hsl(var(--primary) / 0.7), 0 0 3px hsl(var(--primary) / 0.5)",
        }}
      />
    </div>
  );
};
